import { RegistrantTypes, SearchMeditatorActions, TableTitles } from "@/constants";
import { manualRegisterMeditatorRequestSchema } from "@/constants/zod-schemas";
import { useDisclosure } from "@/hooks";
import { useGetMeditationRetreatLevels } from "@/hooks/query-hooks/useLevels";
import { useGetMeditationRetreatByLevels, useGetMeditationRetreatDetail } from "@/hooks/query-hooks/useMeditationRetreats";
import { useManualRegisterMeditator, useSearchMeditatorByPhoneNumber } from "@/hooks/query-hooks/useMeditators";
import { Button, DatePicker, Form, Input, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { NRCInput } from "../common";

type FormValues = {
    user: string;
    bookId?: string;
    levelId?: number;
    name: string;
    phone: string;
    dob: dayjs.Dayjs | string | null;
    nrc?: string;
    address?: string | null;
    state?: string | null;
    township?: string | null;
    meditationEventId: number;
}

export function ManualRegisterMeditator({
    registrantType,
    meditationEventId = null,
    modalType,
}: {
    registrantType?: (typeof RegistrantTypes)[keyof typeof RegistrantTypes],
    modalType: SearchMeditatorActions;
    meditationEventId?: string | null;
}) {

    const [opened, { toggle }] = useDisclosure(false);
    const [activeRegType, setActiveRegType] = useState(registrantType || RegistrantTypes.NEW);
    const [form] = Form.useForm<FormValues>();

    const [phone, setPhone] = useState<string>("");
    const { data: meditator, isLoading: isSearching } = useSearchMeditatorByPhoneNumber(phone, modalType);

    const { data: levels } = useGetMeditationRetreatLevels();

    const [levelId, setLevelId] = useState<number>(0);
    const { data: meditationRetreatsData } = useGetMeditationRetreatByLevels(1, [levelId]);
    const meditationRetreats = meditationRetreatsData?.content || [];

    const { data: meditationRetreat } = useGetMeditationRetreatDetail(meditationEventId || "");

    const [userRegisteredRetreats, setUserRegisteredRetreats] = useState<MeditationRetreat[]>([]);

    const retreatOptions = modalType === SearchMeditatorActions.CANCELLATION_REQUEST ?
        userRegisteredRetreats?.map((el) => ({
            label: el.title + " ( " + el.level.name + " )",
            value: el.id.toString()
        })) : meditationEventId ? [{
            label: meditationRetreat?.title + " ( " + meditationRetreat?.level.name + " )",
            value: meditationEventId
        }] : meditationRetreats.map((retreat) => ({
            label: retreat.title + " ( " + retreat.level.name + " )",
            value: retreat.id.toString()
        }));

    const meditatorOptions = meditator ? [meditator]?.map((el) => ({
        label: el.user.name + " (" + (el.user.phone && el.user.email ? el.user.phone + " - " + el.user.email : el.user.phone ? el.user.phone : el.user.email ? el.user.email : "") + ")",
        value: JSON.stringify(el)
    })) : [];

    const { mutate, isLoading: isRegistering } = useManualRegisterMeditator();
    const onFinish = async (values: FormValues) => {
        const { user, meditationEventId } = values;

        const userData: Meditator = JSON.parse(user);

        const payload: ManualRegisterMeditatorRequest = manualRegisterMeditatorRequestSchema.parse({
            userId: userData.id,
            meditationEventId,
            action: modalType
        });

        mutate(payload, {
            onSuccess: () => {
                toggle();
                form.resetFields();
            }
        })
    };


    const onSelectMeditator = (value: string) => {
        const parsedValue: MeditatorWithMeditationRetreat = JSON.parse(value);

        const { dob, nrcNo, book, level, phone, email } = parsedValue.user;

        form.setFieldsValue({
            ...parsedValue.user,
            user: JSON.stringify(parsedValue.user),
            dob: dayjs(dob),
            nrc: nrcNo,
            bookId: book?.bookNo || "",
            levelId: level.id,
            phone: (phone && email) ? (phone + "/" + email) : phone ? phone : email ? email : "",
        });
        setLevelId(level.id);
        setUserRegisteredRetreats(parsedValue.events || []);
    };

    const onToggleModal = () => {
        toggle();
        form.resetFields();
    }

    return (
        <>
            <Button
                type="default"
                size="large"
                icon={<AiOutlinePlus />}
                onClick={onToggleModal}
            >
                Manual Register
            </Button>
            <Modal
                title="Manual Register"
                open={opened}
                onCancel={onToggleModal}
                width={740}
                okText="အတည်ပြုမည်"
                cancelText="ပယ်ဖျက်မည်"
                centered
                classNames={{
                    header: "!font-poppins"
                }}
                okButtonProps={{
                    size: "large",
                    htmlType: "submit",
                    form: "manual_register",
                    className: "bg-primary-gradient",
                    shape: "round",
                    loading: isRegistering,
                }}
                cancelButtonProps={{
                    size: "large",
                    type: "text",
                    disabled: isRegistering,
                }}
                destroyOnClose
                maskClosable={!isRegistering}
            >
                {/* {
                    !registrantType && (
                        <div
                            className="pb-6 pt-2"
                        >
                            <Segmented
                                options={Object.values(RegistrantTypes)}
                                value={activeRegType}
                                onChange={onTypeChange}
                                size="large"
                                block
                                className="!w-full"
                            />
                        </div>
                    )
                } */}

                <Form
                    form={form}
                    name="manual_register"
                    layout="vertical"
                    onFinish={onFinish}
                    autoComplete="off"
                >

                    <Form.Item
                        label={"User"}
                        name={"user"}
                        hidden
                    >
                        <Input
                            size="large"
                            disabled
                        />
                    </Form.Item>

                    {/* {
                        activeRegType === RegistrantTypes.OLD && ( */}
                    <div
                        className="grid grid-cols-2 gap-4"
                    >
                        <Form.Item
                            label={TableTitles.registrant_book_id}
                            name="bookId"
                        // rules={[{ required: true, message: 'Required!' }]}
                        >
                            <Input
                                size="large"
                                placeholder={TableTitles.registrant_book_id}
                                disabled
                            />
                        </Form.Item>
                        <Form.Item
                            label={TableTitles.retreat_level}
                            name="levelId"
                            rules={[{ required: true, message: 'Required!' }]}
                        >
                            <Select
                                size="large"
                                options={levels?.map((level) => ({
                                    label: level.name,
                                    value: level.id
                                }))}
                                placeholder={TableTitles.retreat_level + " ရွေးချယ်ပါ"}
                                disabled
                            />
                        </Form.Item>
                    </div>
                    {/* )
                    } */}

                    <div
                        className="grid grid-cols-2 gap-4"
                    >
                        <Form.Item
                            label={TableTitles.registrant_name}
                            name="name"
                            rules={[{ required: true, message: 'Required!' }]}
                        >
                            <Input
                                size="large"
                                placeholder={TableTitles.registrant_name}
                                disabled
                            />
                        </Form.Item>
                        <NRCInput
                            form={form}
                            disabled
                        />
                    </div>

                    <div
                        className="grid grid-cols-2 gap-4"
                    >
                        <Form.Item
                            label={TableTitles.registrant_phone + " (or) Email"}
                            name="phone"
                            rules={[{ required: true, message: 'Required!' }]}
                        >
                            <Select
                                placeholder={TableTitles.registrant_phone + " (or) Email ဖြင့်ရှာပါ"}
                                searchValue={phone}
                                value={phone}
                                onSearch={(value) => setPhone(value)}
                                showSearch
                                options={meditatorOptions}
                                onChange={onSelectMeditator}
                                notFoundContent={null}
                                loading={isSearching}
                                size="large"
                            />

                        </Form.Item>
                        <Form.Item
                            label={TableTitles.registrant_dob}
                            name="dob"
                            rules={[{ required: true, message: 'Please input your name!' }]}
                        >
                            <DatePicker
                                size="large"
                                className="w-full !rounded"
                                disabledDate={(current) => current && current > dayjs().endOf('day')}
                                placeholder={TableTitles.registrant_dob}
                                disabled
                            />
                        </Form.Item>
                    </div>

                    {
                        activeRegType === RegistrantTypes.NEW && (
                            <div
                                className="grid grid-cols-2 gap-4"
                            >
                                <Form.Item
                                    label={TableTitles.registrant_nrc}
                                    name="nrc"
                                    rules={[{ required: true, message: 'Required!' }]}
                                >
                                    <Input
                                        size="large"
                                        placeholder={TableTitles.registrant_nrc}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label={TableTitles.registrant_address}
                                    name="address"
                                    rules={[{ required: true, message: 'Required!' }]}
                                >
                                    <Input
                                        size="large"
                                        placeholder={TableTitles.registrant_address}
                                    />
                                </Form.Item>
                            </div>
                        ) && (
                            <>
                                <div
                                    className="grid grid-cols-2 gap-4"
                                >
                                    <Form.Item
                                        label={"တိုင်း‌ဒေသ"}
                                        name="state"
                                    >
                                        <Input
                                            size="large"
                                            placeholder={"တိုင်း‌ဒေသ"}
                                            disabled
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={"မြို့နယ်"}
                                        name="township"
                                    >
                                        <Input
                                            size="large"
                                            placeholder={"မြို့နယ်"}
                                            disabled
                                        />
                                    </Form.Item>
                                </div>
                                <Form.Item
                                    label={"နေရပ်လိပ်စာ"}
                                    name="address"
                                >
                                    <Input.TextArea
                                        size="large"
                                        placeholder={"နေရပ်လိပ်စာ"}
                                        className="!resize-none"
                                        disabled
                                    />
                                </Form.Item>
                            </>
                        )
                    }

                    <Form.Item
                        label={modalType === SearchMeditatorActions.REGISTERATION ? "ဝင်ရောက်လိုသောတရားစခန်း" : "ပယ်ဖျက်လိုသောတရားစခန်း"}
                        name={"meditationEventId"}
                        rules={[{ required: true, message: 'Required!' }]}
                    >
                        <Select
                            size="large"
                            options={retreatOptions}
                            // value={meditationEventId?.toString() || undefined}
                            // defaultValue={meditationEventId || undefined}
                            placeholder="တရားစခန်းရွေးချယ်ပါ"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}
